/*
 * Copyright (C) 2026 - present Instructure, Inc.
 *
 * This file is part of Canvas.
 *
 * Canvas is free software: you can redistribute it and/or modify it under
 * the terms of the GNU Affero General Public License as published by the Free
 * Software Foundation, version 3 of the License.
 *
 * Canvas is distributed in the hope that it will be useful, but WITHOUT ANY
 * WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 * A PARTICULAR PURPOSE. See the GNU Affero General Public License for more
 * details.
 *
 * You should have received a copy of the GNU Affero General Public License along
 * with this program. If not, see <http://www.gnu.org/licenses/>.
 */

import i18next from '@canvas/i18next'
import I18nJs from 'i18n-js'
import './datetimeNamespace' // registers the `en` bundle as a side effect

interface LocalizeNumberOptions {
  precision?: number
  percentage?: boolean
  strip_insignificant_zeros?: boolean
}

function getMergedBundle(): Record<string, unknown> {
  return {
    ...i18next.getResourceBundle('en', 'datetime'),
    ...i18next.getResourceBundle(i18next.language, 'datetime'),
  }
}

// Replacement for the old @canvas/i18n I18n.n()/Scope#localizeNumber(),
// reusing i18n-js's own toNumber/toPercentage formatters verbatim -- only
// the locale-specific delimiter/separator lookup changes.
export function localizeNumber(
  value: number | string,
  options: LocalizeNumberOptions = {},
): string {
  const bundle = getMergedBundle()
  const format = {
    delimiter: (bundle['number.format.delimiter'] as string) ?? ',',
    separator: (bundle['number.format.separator'] as string) ?? '.',
    strip_insignificant_zeros: options.strip_insignificant_zeros || options.precision == null,
    precision: options.precision != null ? options.precision : 5,
  }

  if (value && value.toString().match(/e/)) {
    return value.toString()
  } else if (options.percentage) {
    return I18nJs.toPercentage(value, format)
  } else {
    return I18nJs.toNumber(value, format)
  }
}
